import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { useActionsPresences, useMesPresences } from '@/api/hooks';
import { LIBELLE_CATEGORIE_PRESENCE, LIBELLE_SOURCE_PRESENCE, type CategoriePresence, type LignePresence } from '@/api/types';
import { Chargement, ErreurBloc, Kpi, PageHeader, Vide } from '@/components/common';
import { CategoriePresenceBadge, StatutPeriodeBadge } from '@/components/presences';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { dateFr, nombre } from '@/lib/utils';

const JOURS = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'];
const jourSemaine = (iso: string) => JOURS[new Date(`${iso}T00:00:00Z`).getUTCDay()];

/** Mes présences (lot HP-3) : registre de la période, heures supplémentaires validées, contestation d'une ligne auprès des RH. */
export function MesPresences() {
  const [params, setParams] = useSearchParams();
  const mois = params.get('mois') ?? undefined;
  const { data, isLoading, error } = useMesPresences(mois);
  const [contestation, setContestation] = useState<LignePresence | null>(null);
  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  if (!data.periode) return <div className="space-y-6"><PageHeader titre="Mes présences" /><Vide message="Aucune période de présence ouverte pour le moment." /></div>;
  const p = data.periode;
  const s = data.synthese;
  return (
    <div className="space-y-6">
      <PageHeader
        titre={
          <span className="flex flex-wrap items-center gap-2">
            Mes présences · {p.mois} <StatutPeriodeBadge statut={p.statut} />
          </span>
        }
        sousTitre={`Du ${dateFr(p.debut)} au ${dateFr(p.fin)} (période du 20 au 19, décision D17) · variables attendues avant le ${dateFr(p.dateLimiteVariables)}. Une absence de pointage n'est jamais une absence non rémunérée : signalez toute erreur par une contestation, traitée par les Ressources humaines.`}
        actions={
          <SelectNatif value={p.mois} onChange={(e) => { const n = new URLSearchParams(params); n.set('mois', e.target.value); setParams(n, { replace: true }); }}>
            {data.periodes.map((x) => (
              <option key={x.id} value={x.mois}>
                {x.mois} · {x.statut === 'OUVERTE' ? 'ouverte' : 'clôturée'}
              </option>
            ))}
          </SelectNatif>
        }
      />
      {s && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Kpi label="Jours prestés et missions" valeur={nombre(s.jours.PRESTE + s.jours.MISSION, 1)} detail={`${s.joursOuvrables} jour(s) ouvrable(s) dans la période`} />
          <Kpi label="Congés et incapacité" valeur={nombre(s.jours.CONGE_ANNUEL + s.jours.CONGE_CIRCONSTANCE + s.jours.CONGE_ACCOUCHEMENT + s.jours.INCAPACITE, 1)} detail="rémunération maintenue (D18)" />
          <Kpi label="Jours non rémunérés" valeur={nombre(s.joursDeduits, 1)} detail={`${nombre(s.joursSansAllocation, 1)} jour(s) sans allocation extralégale`} accent={s.joursDeduits > 0 ? 'alerte' : 'ok'} />
          <Kpi label="Heures supplémentaires validées" valeur={`${nombre(s.heuresSup.validees, 2)} h`} detail={`${nombre(s.heuresSup.declarees, 2)} h en attente de validation`} />
        </div>
      )}
      <Card data-testid="mes-presences">
        <CardHeader>
          <CardTitle>Registre de la période</CardTitle>
          <CardDescription>Jours prestés présumés jusqu'au pointage ou à la fiche du responsable, congés approuvés, missions des TDR autorisés, jours fériés et repos ; chaque ligne porte son origine.</CardDescription>
        </CardHeader>
        <CardContent>
          {!data.lignes.length ? (
            <Vide message="Aucune ligne de présence pour cette période." />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Catégorie</TableHead>
                  <TableHead className="hidden md:table-cell">Origine</TableHead>
                  <TableHead className="hidden md:table-cell">Horaire</TableHead>
                  <TableHead className="hidden lg:table-cell">Commentaire</TableHead>
                  {data.droits?.contester && <TableHead />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.lignes.map((l) => (
                  <TableRow key={l.date} className={l.categorie === 'REPOS' ? 'text-muted-foreground' : ''}>
                    <TableCell>
                      {jourSemaine(l.date)} {dateFr(l.date)}
                    </TableCell>
                    <TableCell>
                      <CategoriePresenceBadge categorie={l.categorie} fraction={l.fraction} />
                      {l.anomalie && <div className="text-xs text-amber-700">{l.anomalie}</div>}
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-xs text-muted-foreground">{LIBELLE_SOURCE_PRESENCE[l.source]}</TableCell>
                    <TableCell className="hidden md:table-cell text-xs">
                      {l.heureArrivee ? `${l.heureArrivee} → ${l.heureDepart ?? '—'}` : ''}
                      {l.heuresPrestees !== null ? ` · ${nombre(l.heuresPrestees, 2)} h` : ''}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">{l.commentaire ?? ''}</TableCell>
                    {data.droits?.contester && (
                      <TableCell className="text-right">
                        {l.categorie !== 'REPOS' && l.categorie !== 'FERIE' && (
                          <Button size="sm" variant="ghost" onClick={() => setContestation(l)}>
                            Contester
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      {data.heuresSup.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Heures supplémentaires et astreintes</CardTitle>
            <CardDescription>Déclarées par votre responsable et validées par un autre responsable ou les Ressources humaines ; six premières heures de la semaine à 40 %, puis 70 % (D20).</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Heures</TableHead>
                  <TableHead>Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.heuresSup.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell>{dateFr(h.date)} · {h.semaine}</TableCell>
                    <TableCell>{h.libelleType}{h.motif ? <div className="text-xs text-muted-foreground">{h.motif}</div> : null}</TableCell>
                    <TableCell>{nombre(h.heures, 2)} h</TableCell>
                    <TableCell>
                      <Badge variant={h.statut === 'VALIDEE' ? 'default' : h.statut === 'REFUSEE' ? 'destructive' : 'secondary'}>{h.statut === 'VALIDEE' ? 'Validée' : h.statut === 'REFUSEE' ? 'Refusée' : 'Déclarée'}</Badge>
                      {h.motifDecision ? <div className="text-xs text-muted-foreground">{h.motifDecision}</div> : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
      {contestation && <ContestationDialog periodeId={p.id} ligne={contestation} onClose={() => setContestation(null)} />}
    </div>
  );
}

function ContestationDialog({ periodeId, ligne, onClose }: { periodeId: string; ligne: LignePresence; onClose: () => void }) {
  const actions = useActionsPresences();
  const [motif, setMotif] = useState('');
  const [categorie, setCategorie] = useState<CategoriePresence | ''>('PRESTE');
  const valider = async () => {
    try {
      await actions.contester.mutateAsync({ periodeId, corps: { date: ligne.date, motif, categorieProposee: categorie || undefined } });
      toast.success('Contestation transmise aux Ressources humaines (demande de correction de votre dossier).');
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Contestation impossible.');
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="dialogue-contestation">
        <DialogHeader>
          <DialogTitle>Contester la présence du {dateFr(ligne.date)}</DialogTitle>
          <DialogDescription>Actuellement : {LIBELLE_CATEGORIE_PRESENCE[ligne.categorie]} ({LIBELLE_SOURCE_PRESENCE[ligne.source]}). Votre demande est traitée par les Ressources humaines, qui corrigent le registre.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="space-y-1">
            <Label htmlFor="ct-cat">Catégorie exacte selon vous</Label>
            <SelectNatif id="ct-cat" value={categorie} onChange={(e) => setCategorie(e.target.value as CategoriePresence | '')}>
              <option value="">Je ne sais pas</option>
              {(Object.keys(LIBELLE_CATEGORIE_PRESENCE) as CategoriePresence[]).map((c) => (
                <option key={c} value={c}>
                  {LIBELLE_CATEGORIE_PRESENCE[c]}
                </option>
              ))}
            </SelectNatif>
          </div>
          <div className="space-y-1">
            <Label htmlFor="ct-motif">Explication *</Label>
            <Textarea id="ct-motif" value={motif} onChange={(e) => setMotif(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={valider} disabled={motif.trim().length < 5 || actions.contester.isPending}>Envoyer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
